import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { PackPickService } from '../../api/services/packPickService';
import { IPackingOrderDetail, IPackingStation } from '../../types/api/response/packpickresponse';
import { IPackItem, PackType } from '../../types/api/request/packpickrequest';

interface Props {
  orderNumber: string;
  isOpenDialog: boolean;
  setIsOpenDialog: (open: boolean) => void;
  onCompleted: () => void;
}

const PACK_TYPE_OPTIONS: { value: PackType; label: string }[] = [
  { value: 'WRAP', label: 'Wrap' },
  { value: 'PAPER_BAG', label: 'Paper Bag' },
  { value: 'SMALL_CARTON', label: 'Small Carton' },
  { value: 'BIG_CARTON', label: 'Big Carton' },
  { value: 'BULKY', label: 'Bulky (no packaging)' },
];

export const PickPackDialog = ({
  orderNumber,
  isOpenDialog,
  setIsOpenDialog,
  onCompleted,
}: Props) => {
  const dispatch = useDispatch();
  const [detail, setDetail] = useState<IPackingOrderDetail | null>(null);
  const [stations, setStations] = useState<IPackingStation[]>([]);
  const [loading, setLoading] = useState(false);
  const [startingPick, setStartingPick] = useState(false);
  const [startingPack, setStartingPack] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [selectedStationVauditId, setSelectedStationVauditId] = useState('');
  const [packStart, setPackStart] = useState<string | null>(null);
  const [packItems, setPackItems] = useState<IPackItem[]>([]);
  const [nextPackType, setNextPackType] = useState<PackType>('SMALL_CARTON');

  const loadDetail = async () => {
    setLoading(true);
    try {
      const res = await PackPickService.getOrderDetails(orderNumber);
      setDetail(res);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to load order details.',
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpenDialog) return;
    setDetail(null);
    setPackItems([]);
    setPackStart(null);
    loadDetail();
    PackPickService.getStations()
      .then(setStations)
      .catch(() => setStations([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenDialog, orderNumber]);

  const handleClose = () => setIsOpenDialog(false);

  const handleStartPick = async () => {
    setStartingPick(true);
    try {
      await PackPickService.startPick(orderNumber);
      dispatch(showNotification({ message: 'Picking started.', severity: 'success' }));
      await loadDetail();
      onCompleted();
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to start picking.',
          severity: 'error',
        }),
      );
    } finally {
      setStartingPick(false);
    }
  };

  const handleStartPack = async () => {
    if (!selectedStationVauditId) {
      dispatch(showNotification({ message: 'Select a packing station.', severity: 'error' }));
      return;
    }
    setStartingPack(true);
    try {
      await PackPickService.startPack(orderNumber, { station_vaudit_id: selectedStationVauditId });
      setPackStart(new Date().toISOString());
      dispatch(showNotification({ message: 'Packing started.', severity: 'success' }));
      await loadDetail();
      onCompleted();
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to start packing.',
          severity: 'error',
        }),
      );
    } finally {
      setStartingPack(false);
    }
  };

  const handleAddPackItem = () => {
    setPackItems((prev) => [
      ...prev,
      { id: `${nextPackType}-${prev.length + 1}-${Date.now()}`, type: nextPackType },
    ]);
  };

  const handleRemovePackItem = (id: string) => {
    setPackItems((prev) => prev.filter((item) => item.id !== id));
  };

  const cartItemsForRequest = useMemo(
    () =>
      (detail?.cart_items ?? []).map((item) => ({
        sku: item.sku,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
      })),
    [detail],
  );

  const handleCompletePack = async () => {
    if (packItems.length === 0) {
      dispatch(
        showNotification({
          message: 'Add at least one package before completing.',
          severity: 'error',
        }),
      );
      return;
    }
    setCompleting(true);
    try {
      await PackPickService.completePack(orderNumber, {
        pack_items: packItems,
        cart_items: cartItemsForRequest,
        audit_details: {
          station_vaudit_id: selectedStationVauditId || null,
          video_url: null,
          pack_start: packStart,
          pack_end: new Date().toISOString(),
        },
      });
      dispatch(showNotification({ message: 'Packing completed.', severity: 'success' }));
      onCompleted();
      setIsOpenDialog(false);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to complete packing.',
          severity: 'error',
        }),
      );
    } finally {
      setCompleting(false);
    }
  };

  const handleCancelPack = async () => {
    setCancelling(true);
    try {
      await PackPickService.cancelPack(orderNumber, {
        audit_details: {
          station_vaudit_id: selectedStationVauditId || null,
          video_url: null,
          pack_start: packStart,
          pack_end: new Date().toISOString(),
        },
      });
      dispatch(showNotification({ message: 'Packing cancelled.', severity: 'success' }));
      onCompleted();
      setIsOpenDialog(false);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to cancel packing.',
          severity: 'error',
        }),
      );
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog
      open={isOpenDialog}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: { sx: { maxHeight: '90vh' } },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
      >
        <Typography fontWeight={600}>Pick / Pack — {orderNumber}</Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'grey.700' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {loading || !detail ? (
        <DialogContent sx={{ pt: 2 }}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      ) : (
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Status: {detail.status} · Delivery: {detail.delivery_type}
            </Typography>
          </Box>

          {/* Items — always shown read-only, this is what vAudit itself would see */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Items
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="center">Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.cart_items.map((item) => (
                  <TableRow key={item.sku}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.sku}</TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Divider />

          {detail.status === 'APPROVED' && (
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={handleStartPick} disabled={startingPick}>
                {startingPick ? 'Starting...' : 'Start Pick'}
              </Button>
            </Box>
          )}

          {detail.status === 'PICKING' && (
            <Box display="flex" gap={2} alignItems="center" justifyContent="flex-end">
              <TextField
                select
                size="small"
                label="Station"
                value={selectedStationVauditId}
                onChange={(e) => setSelectedStationVauditId(e.target.value)}
                sx={{ minWidth: 220 }}
              >
                {stations.map((station) => (
                  <MenuItem key={station.id} value={String(station.id)}>
                    {station.name}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="contained" onClick={handleStartPack} disabled={startingPack}>
                {startingPack ? 'Starting...' : 'Start Pack'}
              </Button>
            </Box>
          )}

          {detail.status === 'PACKING' && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Packages Used
              </Typography>
              <Box display="flex" gap={1} mb={1.5} alignItems="center">
                <TextField
                  select
                  size="small"
                  value={nextPackType}
                  onChange={(e) => setNextPackType(e.target.value as PackType)}
                  sx={{ minWidth: 200 }}
                >
                  {PACK_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant="outlined" onClick={handleAddPackItem}>
                  Add Package
                </Button>
              </Box>

              {packItems.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                  {packItems.map((item) => (
                    <Chip
                      key={item.id}
                      label={PACK_TYPE_OPTIONS.find((o) => o.value === item.type)?.label}
                      onDelete={() => handleRemovePackItem(item.id)}
                      deleteIcon={<DeleteOutlineIcon />}
                    />
                  ))}
                </Box>
              )}

              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button color="error" onClick={handleCancelPack} disabled={cancelling}>
                  {cancelling ? 'Cancelling...' : 'Cancel Pack'}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleCompletePack}
                  disabled={completing || packItems.length === 0}
                >
                  {completing ? 'Completing...' : 'Complete Pack'}
                </Button>
              </Box>
            </Box>
          )}

          {!['APPROVED', 'PICKING', 'PACKING'].includes(detail.status) && (
            <Typography variant="body2" color="text.secondary">
              This order has moved past picking/packing (status: {detail.status}).
            </Typography>
          )}

          <Divider />

          <Box display="flex" justifyContent="flex-end">
            <Button onClick={handleClose}>Close</Button>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
};
